import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ChevronDownIcon, BuildingIcon, SearchIcon } from 'lucide-react';
export interface Company {
  id: string;
  name: string;
  logo?: string;
}
interface CompanySelectorProps {
  companies: Company[];
  selectedCompanies: Company[];
  onChange: (companies: Company[]) => void;
}
export function CompanySelector({
  companies,
  selectedCompanies,
  onChange
}: CompanySelectorProps) {
  const {
    t
  } = useTranslation('analytics');
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Filter companies based on search query
  const filteredCompanies = companies.filter(company => company.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Select a single company (replaces any previous selection)
  const selectCompany = (company: Company) => {
    onChange([company]);
    setIsOpen(false);
    setSearchQuery('');
  };
  // Display text based on selection
  const displayText = () => {
    if (selectedCompanies.length === 0) {
      return 'Select Company';
    }
    return selectedCompanies[0].name;
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1541" data-mp="2a21e4xhdvsj6">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between min-w-40 max-w-64 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1542" data-mp="261r9pp98pa6f">
        <div className="flex items-center min-w-0 flex-1" data-id="element-1543" data-mp="vxr6ocujhr8f">
          {selectedCompanies.length === 1 && selectedCompanies[0].logo ? <img src={selectedCompanies[0].logo} alt={selectedCompanies[0].name} className="w-5 h-5 rounded-full mr-2 flex-shrink-0" data-id="element-1544" data-mp="10xm0op9m2wxp" /> : <BuildingIcon size={16} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-1545" data-mp="r3d8k2oht602" />}
          <span className="truncate" data-id="element-1546" data-mp="i5kt8yirtyco">{displayText()}</span>
        </div>
        <ChevronDownIcon size={16} className="ml-2 flex-shrink-0" data-id="element-1547" data-mp="37sdc59bb7xy" />
      </button>
      {isOpen && <div className="absolute left-0 z-10 w-72 mt-1 bg-white border border-gray-200 rounded-md shadow-lg" data-id="element-1548" data-mp="86vlhykl9ua5">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200" data-id="element-1549" data-mp="1xmuvtizy1prd">
            <div className="relative" data-id="element-1550" data-mp="22kwjyic88f9p">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1551" data-mp="22jtnluw4xkme">
                <SearchIcon size={14} className="text-gray-400" data-id="element-1552" data-mp="3n3p3njrf35p4" />
              </div>
              <input type="search" aria-label={t('components.CompanySelector.attrs.search_companies')} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={t('components.CompanySelector.attrs.search_companies')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1553" data-mp="3i4m0y5ywtbkb" />
            </div>
          </div>
          {/* Companies list */}
          <div className="max-h-60 overflow-y-auto" data-id="element-1554" data-mp="enwzuzi3iu21">
            {filteredCompanies.length > 0 ? filteredCompanies.map(company => {
          const isSelected = selectedCompanies.some(c => c.id === company.id);
          return <div key={company.id} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-1555" data-mp="2hxqlmxtpdw58">
                    <div className="flex items-center flex-1" data-id="element-1556" data-mp="2wrvdh7ghkscm">
                      {company.logo ? <img src={company.logo} alt={company.name} className="w-6 h-6 rounded-full mr-2" data-id="element-1557" data-mp="1z6h90fomyr9f" /> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2" data-id="element-1558" data-mp="291hkneq2upgz">
                          <span className="text-xs font-medium text-gray-600" data-id="element-1559" data-mp="1geolh0sc5y93">
                            {company.name.charAt(0)}
                          </span>
                        </div>}
                      <span className="text-sm" data-id="element-1560" data-mp="2j5a42f2rb3y">{company.name}</span>
                    </div>
                    <div className="ml-2" data-id="element-1561" data-mp="3ulwgkvx5jsvi">
                      {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-1562" data-mp="3pncvexqdul47" />}
                    </div>
                  </div>;
        }) : <div className="px-3 py-2 text-sm text-gray-500" data-id="element-1563" data-mp="3fp5cwagg3v03">
                {t('components.CompanySelector.no_companies_found')}
              </div>}
          </div>
        </div>}
    </div>;
}